import {Injectable} from "@angular/core";
import {Router} from "@angular/router";
import {HttpClient} from "@angular/common/http";
import {BehaviorSubject, Observable} from "rxjs";
import {Response} from './base.service';
import { ToastrService } from 'ngx-toastr';
import {EnvironmentService} from "./environment.service";
import {IClassTimeTable} from "./timetable.service";

export interface SchoolYearCount {
  schoolId: number;
  yearId: number;
  count: number;
  schoolType: SchoolType;
  secondaryCount: number;
}

export interface SchoolTerritoryUpdateRequest {
  schoolId: number;
  territoryId: number;
}

export interface SchoolTypeUpdateRequest {
  schoolId: number;
  schoolType: SchoolType;
}

export interface ISchoolYearResponse {
  id: number;           // Long in Java maps to number in TypeScript
  yearReferenceId: number;
  year: string;
  active: boolean;
  count: number; // int in Java maps to number in TypeScript
  amount: number;      // double in Java maps to number in TypeScript
  canGenerate: boolean;
}

export interface ISchoolYear {
  id: number;
  description: string;
  active: boolean;
}


export interface ILoadingParam {
  loadParent: boolean;
  schoolCode: string;
  year: number;
  yearName: string;
  loadStudent: boolean;
  loadTeacher: boolean;
  loadCoef: boolean;
  loadSubject: boolean;
}

export interface UserResponse {
  id: number;
  regNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
}

export interface ILocality {
  id: number;
  name: string;
  manager: string;
  email: string;
}

export enum SchoolType {
  BASE = 'BASE',
  SECONDARY = 'SECONDARY',
}

export interface ISchoolListElt {
  debtAmount: number;
  years: ISchoolYearResponse[];
  schoolType: SchoolType,
  count: number;
  enabled: boolean;
  id: number;
  name: string;
  type: string;
  address: string;
  localityId: number;
  localityName: string;
  postalCode: string;
  city: string;
  area: string;
  country: string;
  phoneNumber: string;
  founder: UserResponse | undefined;
  authorisationNumber: string;
  authorizationDate: string;
  email: string;
  photoUrl : string
  // local
  myFile: string | undefined;
}

export interface IStudentPayment {
  id : string;
  name : string;
  paidAmount : number;
  expectedAmount : number;
  percent : number;
}

export interface ISchool {
  postalCode: string;
  country: string;

  firstName: string;
  lastName: string;
  gender: string;
  dateOfBirth: string;
  birthPlace : string;
  phone: string;
  email: string;

  address: string;
  city: string;
  uid: number;
  governmentNumber : string;
  bloodGroup: string,

  fatherFirstName: string;
  fatherLastName: string;
  fatherPhone: string;
  fatherEmail: string;
  fatherOccupation: string;

  motherFirstName: string;
  motherLastName: string;
  motherPhone: string;
  motherEmail: string;
  motherOccupation: string;

  myFile: string;
  languagesKnown: string[];
  origin : string;

}

const schoolListElt : ISchoolListElt = {
  years: [],
  address: "",
  area: "",
  authorisationNumber: "",
  authorizationDate: "",
  city: "",
  country: "",
  email: "",
  founder: undefined,
  id: 0,
  localityId: 0,
  localityName: "",
  name: "",
  phoneNumber: "",
  postalCode: "",
  myFile: undefined,
  enabled: true,
  photoUrl: "",
  count : 0,
  debtAmount: 0,
  type: "",
  schoolType: SchoolType.BASE
}

export class StudentRenewRequest {
  studentRegistrationNumber: string | undefined;
  classroomCode: string | undefined;
  fees: number | undefined;
  initialCost: number | undefined;
}

export interface StudentRenewResponse {
  id : number;
  studentRegistrationNumber: string | undefined;
  classroomCode: string | undefined;
  fees: number | undefined;
  initialCost: number | undefined;
}

export interface IStudentClass {
  name: string;

}

export interface IExamEvalElt {
  session: string;
  id: number
  idNo: number;
  examId: number;
  subjectCode : string;
  controlContinuous : boolean;
  studentRegNumber: string;
  grade : number;
  maxGrade : number;
  comment: string;
  description: string;
  date: string;
}

@Injectable({
  providedIn: 'root',
})
export class SchoolService {

  public schools: BehaviorSubject<ISchoolListElt[]> = new BehaviorSubject<ISchoolListElt[]>([]);

  constructor(private http: HttpClient,
              private env: EnvironmentService) { }

  public registerSchool(studentRegistration: ISchoolListElt): Observable<Response<ISchool>> {
    return this.http.post<Response<ISchool>>(`${this.env.apiBaseUrl()}/api/school/register`, studentRegistration)
  }

  public updateSchool(schoolListElt: ISchoolListElt): Observable<Response<ISchool>> {
    return this.http.post<Response<ISchool>>(`${this.env.apiBaseUrl()}/api/school/update`, schoolListElt)
  }

  public getAllSchool(territoryId: number) {
    let url = `${this.env.apiBaseUrl()}/api/school?territory=${territoryId}`
    if (territoryId === -1) {
      url = `${this.env.apiBaseUrl()}/api/school`
    }
    return this.http.get<Response<Array<ISchoolListElt>>>(url)
  }

  searchSchool(searchId: string, searchName: string, classroomCode: string)  {
    // check param and append as query param
    let query = '';
    if (searchId !== '') {
      query += ('id=' + searchId)
    }
    if (searchName !== '') {
      if (query !== '')
        query+= '&'
      query += ('name=' + searchName)
    }
    if (classroomCode !== '') {
      if (query !== '')
        query+= '&'
      query += ('class=' + classroomCode)
    }
    return this.http.get<Response<Array<ISchoolListElt>>>(`${this.env.apiBaseUrl()}/api/sm-students/search?` + query)
  }

  public getSchool(regNumber : number) {
    return this.http.get<Response<ISchoolListElt>>(`${this.env.apiBaseUrl()}/api/school/` + regNumber)
  }

  public getStudentByRegNumber(regNumber : string) {
    return this.http.get<Response<ISchoolListElt>>(`${this.env.apiBaseUrl()}/api/sm-students` + '/' + regNumber)
  }

  public getStudentClassDetails(regNumber: string): Observable<IStudentClass[]> {
    return this.http.get<IStudentClass[]>(`${this.env.apiBaseUrl()}/api/sm-students/class-details/${regNumber}`);
  }

  public getNextRegistrationNumber():  Observable<Response<string>> {
    return this.http.get<Response<string>>(`${this.env.apiBaseUrl()}/api/sm-students/nextRegistrationNumber`)
  }

  generateReportCard(regNumber: string , classroomCode: string, session : string, fullYear: boolean): Observable<Blob> {
    const options = { responseType: 'blob' as 'json' };
    return this.http.post<Blob>(`${this.env.apiBaseUrl()}/api/report-cards/generate`, {
      studentRegNumber : regNumber,
      classroomCode : classroomCode,
      fullYear: fullYear,
      session : session
    }, options)
  }

  generateReportCardForClassroom(classroomCode: string, session : string, fullYear: boolean): Observable<Blob> {
    const options = { responseType: 'blob' as 'json' };
    return this.http.post<Blob>(`${this.env.apiBaseUrl()}/api/report-cards/` + classroomCode + `/session/` + session + `/full-year/`+ fullYear, null, options)
  }

  getCurrentClass(registrationNumber: string) {
    return this.http.get<Response<StudentRenewResponse>>(`${this.env.apiBaseUrl()}/api/sm-students/currentClass/` + registrationNumber)
  }

  uploadPicture(registrationNumber: number, file: any) {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<Response<string>>(`${this.env.apiBaseUrl()}/api/school/` + registrationNumber + `/upload`, formData);
  }

  activate(registrationNumber: number, b: boolean) {
    return this.http.post<Response<ISchoolListElt>>(`${this.env.apiBaseUrl()}/api/sm-students/` + registrationNumber + `/activate/` + b, null)
  }

  delete(regNumber: number, b: boolean) {
    return this.http.post<Response<ISchoolListElt>>(`${this.env.apiBaseUrl()}/api/sm-students/` + regNumber + `/delete/` + b, null)
  }

  printList() {
    const options = { responseType: 'blob' as 'json' };
    return this.http.get<Blob>(`${this.env.apiBaseUrl()}/api/sm-students/active`, options)
  }

  promote(regNumber: string, currentClass: string, toClass: string) {
    return this.http.post<Response<string>>(`${this.env.apiBaseUrl()}/api/sm-students/promote/` + regNumber + `/fromClass/` + currentClass + '/toClass/' + toClass, null)
  }

  deleteProfilePicture(uid: number) {
    return this.http.post<Response<string>>(`${this.env.apiBaseUrl()}/api/sm-students/` + uid + `/delete`, null);
  }

  getAllSchoolYears() {
    return this.http.get<Response<Array<ISchoolYear>>>(`${this.env.apiBaseUrl()}/api/school/years`)
  }

  loadConfiguration(file: File, data: ILoadingParam) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('loadStudent', data.loadStudent.toString());
    formData.append('loadParent', data.loadParent.toString());
    formData.append('year', data.year.toString());
    formData.append('schoolCode', data.schoolCode.toString());
    formData.append('loadTeacher', data.loadTeacher.toString());
    formData.append('loadSubject', data.loadSubject.toString());
    formData.append('loadCoef', data.loadCoef.toString());
    return this.http.post<Response<Array<IClassTimeTable>>>(`${this.env.apiBaseUrl()}/api/academic/upload-configuration/${data.schoolCode}/${data.year}`, formData );
  }

  updateSchoolCount(renew: SchoolYearCount) {
    return this.http.post<Response<ISchoolListElt>>(`${this.env.apiBaseUrl()}/api/school/update-count`, renew);
  }

  getLocalities() {
    return this.http.get<Response<ILocality[]>>(`${this.env.apiBaseUrl()}/api/school/locality`);
  }

  updateSchoolTerritory(value : SchoolTerritoryUpdateRequest) {
    return this.http.post<Response<ISchoolListElt>>(`${this.env.apiBaseUrl()}/api/school/update-territory`, value);
  }

  updateSchoolType(value : SchoolTypeUpdateRequest) {
    return this.http.post<Response<ISchoolListElt>>(`${this.env.apiBaseUrl()}/api/school/update-type`, value);
  }

  printSchoolCertificate(schoolId: number, yearCode: number) {
    const options = { responseType: 'blob' as 'json' };
    return this.http.get<Blob>(`${this.env.apiBaseUrl()}/api/school/generate/${schoolId}/${yearCode}`, options)
  }

  printFounderCertificate(regNumber: number) {
    const options = { responseType: 'blob' as 'json' };
    return this.http.get<Blob>(`${this.env.apiBaseUrl()}/api/school/generate/founder/${regNumber}`, options)
  }

}
